/**
 * Hook for Gemini Live API Chat Integration (Direct Connection)
 *
 * Features:
 * - Direct client-to-Gemini connection using ephemeral tokens
 * - Affective dialog (emotion-aware responses)
 * - Always-listening mode with automatic VAD
 * - Session pause/resume (within 2hr window)
 * - Token usage tracking in Firebase
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GeminiDirectClient } from '../services/geminiDirectClient';
import { MAX_MESSAGES, MAX_MESSAGES_WITH_AUDIO } from '../constants/chat';
// MED-006: logger utility available for future console.log replacements
// import { logger } from '../utils/logger';
import {
  createSessionUsage,
  updateSessionUsage,
  finalizeSessionUsage
} from '../services/firebase/tokenUsage';
import {
  saveReviewItem,
  startReviewLesson,
} from '../services/firebase/sessionData';
import { getWebAudioManager } from '../services/webAudioBridge';
import type { AIRole } from '../types/ai-role';
import type { UsageMetadata } from '../types/gemini';
import type {
  MarkForReviewParams,
  MarkTaskCompleteParams,
  ShowSessionSummaryParams,
} from '../types/functions';
import type { BadgeDefinition } from '../types/badges';

export interface ChatMessage {
  id: number;
  text: string;
  isUser: boolean;
  isWhisper: boolean;
  translation?: string;
  audioData?: string;
}

// Audio playback request for play_student_audio function
export interface AudioPlaybackRequest {
  url: string;
  itemId: string;
}

export interface UseGeminiChatResult {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;

  // Listening state (always true when connected, unless muted)
  isListening: boolean;
  isMuted: boolean;

  // Pause state
  isPaused: boolean;
  canResume: boolean;

  // Playback state
  isPlaying: boolean;

  // Token usage
  tokenUsage: UsageMetadata;

  // Messages
  messages: ChatMessage[];

  // Session summary (from function call)
  sessionSummary: ShowSessionSummaryParams | null;

  // Newly earned badges (from session completion)
  newBadges: BadgeDefinition[];

  // Session timing
  sessionStartTime: Date | null;

  // Session timed out (Gemini didn't respond to end prompt)
  sessionTimedOut: boolean;

  // Session resumption failed (context lost due to server error)
  resumptionFailed: boolean;

  // Session mistakes (for automatic session summary)
  sessionMistakes: Array<{
    userSentence: string;
    correction: string;
    errorType: string;
  }>;

  // Audio playback for review lessons (play_student_audio function)
  audioToPlay: AudioPlaybackRequest | null;
  onAudioPlaybackComplete: () => void;
  onAudioPlaybackError: (error: Error) => void;

  // Actions
  toggleMute: () => void;
  disconnect: () => Promise<void>;
  pauseSession: () => Promise<void>;
  resumeSession: () => Promise<void>;
  sendTextMessage: (text: string) => void;
  sendSilentText: (text: string) => void;  // Send to Gemini without showing in chat
  clearMessages: () => void;
  reconnect: () => void;
  startNewSession: () => void;
  updateSystemPrompt: (role: AIRole) => void;
  clearSessionSummary: () => void;
  clearNewBadges: () => void;
  nudgeResponse: () => void;  // Nudge Gemini when it doesn't respond

  // Exposed for session lifecycle hook
  startAudioStreaming: () => Promise<void>;
  stopAudioStreaming: () => Promise<void>;
}

export function useGeminiChat(
  initialRole?: AIRole,
  userId?: string,
  onTaskComplete?: (taskId: string) => void,
  voiceName?: string,
  userDocumentReady?: boolean,  // True when user document has loaded (voice preference available)
  onAiTurnComplete?: () => void  // Called when AI finishes speaking (for session end timing)
): UseGeminiChatResult {
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Always-listening state
  const [isListening, setIsListening] = useState(false);
  const [isMuted, setIsMuted] = useState(false);

  // Pause state
  const [isPaused, setIsPaused] = useState(false);
  const [canResume, setCanResume] = useState(false);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);

  // Token usage
  const [tokenUsage, setTokenUsage] = useState<UsageMetadata>({
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0
  });

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Session summary state (from show_session_summary function call)
  const [sessionSummary, setSessionSummary] = useState<ShowSessionSummaryParams | null>(null);

  // Newly earned badges state
  const [newBadges, setNewBadges] = useState<BadgeDefinition[]>([]);

  // Session timing
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);

  // Session timeout state (when Gemini doesn't respond to end prompt)
  const [sessionTimedOut, _setSessionTimedOut] = useState(false);

  // Session resumption failed state (context lost due to server error)
  const [resumptionFailed, setResumptionFailed] = useState(false);

  // Audio playback state for play_student_audio function
  const [audioToPlay, setAudioToPlay] = useState<AudioPlaybackRequest | null>(null);

  // Refs
  const clientRef = useRef<GeminiDirectClient | null>(null);
  const roleRef = useRef<AIRole | undefined>(initialRole);
  const messageIdRef = useRef(0);
  const isStreamingRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);
  const userIdRef = useRef(userId || `user_${Date.now()}`);
  const voiceNameRef = useRef(voiceName);
  const missionIdRef = useRef<string | null>(null);

  // Keep voiceNameRef in sync with voiceName prop
  useEffect(() => {
    voiceNameRef.current = voiceName;
  }, [voiceName]);

  // Track userDocumentReady
  const userDocumentReadyRef = useRef(userDocumentReady);
  useEffect(() => {
    userDocumentReadyRef.current = userDocumentReady;
  }, [userDocumentReady]);

  // Keep onAiTurnComplete ref in sync
  useEffect(() => {
    onAiTurnCompleteRef.current = onAiTurnComplete;
  }, [onAiTurnComplete]);
  const recentReviewItems = useRef<Map<string, number>>(new Map()); // Deduplication map: key -> timestamp
  const sessionMistakesRef = useRef<Array<{
    userSentence: string;
    correction: string;
    errorType: string;
  }>>([]); // Track mistakes for end-of-session prompt
  const audioPlaybackResolverRef = useRef<{ resolve: () => void; reject: (e: Error) => void } | null>(null);
  const onAiTurnCompleteRef = useRef(onAiTurnComplete); // Callback for when AI finishes speaking
  const outputTranscriptionBuffer = useRef<string>(''); // Accumulate AI transcription chunks
  const inputTranscriptionBuffer = useRef<string>(''); // Accumulate user transcription chunks
  const turnCompleteTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounce turn complete processing
  const lastTranscriptionTimeRef = useRef<number>(0); // Track when last transcription was received

  // Audio replay support - accumulate AI audio for each turn (store as Uint8Array for proper concatenation)
  const aiAudioChunksRef = useRef<Uint8Array[]>([]); // Accumulate AI audio chunks as binary during response

  // Function agent support - track context for /api/function-agent calls
  const recentTurnsRef = useRef<Array<{ role: 'user' | 'ai'; text: string }>>([]);
  const isReviewLessonRef = useRef<boolean>(false);
  const sessionContextRef = useRef<{
    level: string;
    tasks?: Array<{ id: string; text: string }>;
  }>({ level: 'B1' });
  const lastUserTranscriptionRef = useRef<string>(''); // Track user text for function agent

  // Helper to convert blob to base64
  const blobToBase64 = useCallback((blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:audio/wav;base64,")
        const base64 = result.split(',')[1] || result;
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  /**
   * Generate next message ID
   */
  const getNextMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  }, []);

  /**
   * Add message to chat (HIGH-003: with message limits to prevent memory issues)
   */
  const addMessage = useCallback((
    text: string,
    isUser: boolean,
    isWhisper: boolean = false,
    translation?: string,
    audioData?: string
  ) => {
    const message: ChatMessage = {
      id: getNextMessageId(),
      text,
      isUser,
      isWhisper,
      translation,
      audioData
    };

    setMessages(prev => {
      let updated = [...prev, message];

      // Enforce total message limit
      if (updated.length > MAX_MESSAGES) {
        updated = updated.slice(-MAX_MESSAGES);
      }

      // Limit messages with audio to reduce memory usage
      const messagesWithAudio = updated.filter(m => m.audioData);
      if (messagesWithAudio.length > MAX_MESSAGES_WITH_AUDIO) {
        const toRemoveAudio = messagesWithAudio.length - MAX_MESSAGES_WITH_AUDIO;
        let removed = 0;
        updated = updated.map(m => {
          if (m.audioData && removed < toRemoveAudio) {
            removed++;
            return { ...m, audioData: undefined };
          }
          return m;
        });
      }

      return updated;
    });

    return message;
  }, [getNextMessageId]);

  /**
   * HIGH-004: Async base64 conversion to prevent main thread blocking
   * Uses FileReader/Blob which is non-blocking for large buffers
   */
  const arrayBufferToBase64Async = useCallback((buffer: Uint8Array): Promise<string> => {
    return new Promise((resolve, reject) => {
      // Create a copy to ensure it's a regular ArrayBuffer, not SharedArrayBuffer
      const arrayBuffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength) as ArrayBuffer;
      const blob = new Blob([arrayBuffer]);
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/octet-stream;base64,")
        const base64 = result.split(',')[1] || '';
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  /**
   * Play audio response
   */
  const playAudioResponse = useCallback(async (audioData: ArrayBuffer) => {
    try {
      const audioManager = (await import('../services/webAudioBridge')).getWebAudioManager();
      await audioManager.initialize();

      // Convert ArrayBuffer to base64 using async method
      const bytes = new Uint8Array(audioData);
      const base64 = await arrayBufferToBase64Async(bytes);

      setIsPlaying(true);
      await audioManager.playAudioChunk(base64);
      setIsPlaying(false);
    } catch (error) {
      console.error('[Audio] Playback error:', error);
      setIsPlaying(false);
    }
  }, [arrayBufferToBase64Async]);

  /**
   * Start streaming audio to Gemini
   */
  const startAudioStreaming = useCallback(async () => {
    if (isStreamingRef.current || !clientRef.current?.connected) {
      return;
    }

    try {
      const audioManager = (await import('../services/webAudioBridge')).getWebAudioManager();
      await audioManager.initialize();
      await audioManager.startRecording((base64Data: string) => {
        if (!isMuted && clientRef.current?.connected) {
          clientRef.current.sendAudioBase64(base64Data);
        }
      });

      isStreamingRef.current = true;
      setIsListening(true);
      console.log('[Audio] Started streaming');
    } catch (error) {
      console.error('[Audio] Failed to start streaming:', error);
      setConnectionError('Microphone access denied');
    }
  }, [isMuted]);

  /**
   * Stop streaming audio
   */
  const stopAudioStreaming = useCallback(async () => {
    if (!isStreamingRef.current) return;

    try {
      const audioManager = (await import('../services/webAudioBridge')).getWebAudioManager();
      audioManager.stopRecording();
    } catch {
      // Ignore errors during cleanup
    }

    isStreamingRef.current = false;
    setIsListening(false);
    console.log('[Audio] Stopped streaming');
  }, []);

  /**
   * Callback for when audio playback completes successfully
   */
  const onAudioPlaybackComplete = useCallback(() => {
    console.log('[Gemini] Audio playback completed');
    setAudioToPlay(null);
    if (audioPlaybackResolverRef.current) {
      audioPlaybackResolverRef.current.resolve();
      audioPlaybackResolverRef.current = null;
    }
  }, []);

  /**
   * Callback for when audio playback fails
   */
  const onAudioPlaybackError = useCallback((error: Error) => {
    console.error('[Gemini] Audio playback error:', error);
    setAudioToPlay(null);
    if (audioPlaybackResolverRef.current) {
      audioPlaybackResolverRef.current.reject(error);
      audioPlaybackResolverRef.current = null;
    }
  }, []);

  /**
   * Execute a function call from the function agent
   * Handles: mark_for_review, mark_task_complete
   * @param call - The function call to execute
   * @param turnAudioBlob - Pre-captured audio blob from turn completion (before buffer cleared)
   */
  const executeFunctionCall = useCallback(async (
    call: { name: string; args: Record<string, unknown> },
    turnAudioBlob: Blob | null
  ) => {
    console.log('[FunctionAgent] Executing:', call.name, call.args);

    // Normalize function name (Gemini sometimes returns 'mark_forreview' without underscore)
    const functionName = call.name.replace('mark_forreview', 'mark_for_review');

    switch (functionName) {
      case 'mark_for_review': {
        const params = call.args as unknown as MarkForReviewParams;

        // Validate required fields
        if (!params.user_sentence || !params.error_type) {
          console.warn('[FunctionAgent] Invalid mark_for_review params');
          return;
        }

        // Deduplication (30-second window)
        const dedupeKey = `${params.user_sentence}|${params.error_type}`;
        const now = Date.now();
        if (recentReviewItems.current.has(dedupeKey)) {
          const lastSeen = recentReviewItems.current.get(dedupeKey)!;
          if (now - lastSeen < 30000) {
            console.log('[FunctionAgent] Skipping duplicate mark_for_review');
            return;
          }
        }
        recentReviewItems.current.set(dedupeKey, now);

        // Use pre-captured audio from turn completion (captured before buffer was cleared)
        const audioBlob = turnAudioBlob;
        console.log('[FunctionAgent] Using pre-captured audio:',
          audioBlob ? `${(audioBlob.size / 1024).toFixed(1)} KB` : 'null');

        // Save to Firebase
        if (sessionIdRef.current) {
          await saveReviewItem(
            sessionIdRef.current,
            userIdRef.current,
            params,
            missionIdRef.current || undefined,
            audioBlob
          );

          // Track mistake for session summary
          sessionMistakesRef.current.push({
            userSentence: params.user_sentence,
            correction: params.correction,
            errorType: params.error_type,
          });
        }
        break;
      }

      case 'mark_task_complete': {
        const params = call.args as unknown as MarkTaskCompleteParams;
        if (params.task_id && onTaskComplete) {
          console.log('[FunctionAgent] ✅ Task completed:', params.task_id);
          onTaskComplete(params.task_id);
        }
        break;
      }

      default:
        console.warn('[FunctionAgent] Unknown function:', call.name);
    }
  }, [onTaskComplete]);

  /**
   * Process turn for function calling via backend agent
   * Called after each turn complete with transcriptions
   */
  const processTurnForFunctions = useCallback(async (
    userTranscription: string,
    aiTranscription: string,
    turnAudioBlob: Blob | null
  ) => {
    // Skip if review lesson (voice-only)
    if (isReviewLessonRef.current) {
      return;
    }

    // Skip if no meaningful content
    if (!userTranscription.trim() && !aiTranscription.trim()) {
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/function-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_context: sessionContextRef.current,
          ai_transcription: aiTranscription,
          user_transcription: userTranscription,
          recent_history: recentTurnsRef.current.slice(-3),
          is_review_lesson: isReviewLessonRef.current
        })
      });

      if (!response.ok) {
        console.warn('[FunctionAgent] Request failed:', response.status);
        return;
      }

      const { function_calls, processing_time_ms } = await response.json();
      console.log(`[FunctionAgent] Processed in ${processing_time_ms}ms, ${function_calls.length} calls`);

      // Execute function calls with pre-captured audio
      for (const call of function_calls) {
        await executeFunctionCall(call, turnAudioBlob);
      }

      // Update recent turns for context
      if (userTranscription.trim()) {
        recentTurnsRef.current.push({ role: 'user', text: userTranscription });
      }
      if (aiTranscription.trim()) {
        recentTurnsRef.current.push({ role: 'ai', text: aiTranscription });
      }
      // Keep only last 6 entries
      if (recentTurnsRef.current.length > 6) {
        recentTurnsRef.current = recentTurnsRef.current.slice(-6);
      }

    } catch (error) {
      // Non-blocking: log but continue session
      console.error('[FunctionAgent] Error:', error);
    }
  }, [executeFunctionCall]);


  /**
   * Clear session summary (after modal is closed)
   */
  const clearSessionSummary = useCallback(() => {
    setSessionSummary(null);
  }, []);

  /**
   * Clear newly earned badges (after badge modal is closed)
   */
  const clearNewBadges = useCallback(() => {
    setNewBadges([]);
  }, []);

  /**
   * Initialize and connect to Gemini
   */
  const connect = useCallback(async () => {
    if (clientRef.current?.connected) {
      console.log('[Gemini] Already connected');
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    // Use the raw system prompt from the role (teacher's custom prompt)
    // Tool instructions are now auto-injected by the backend PromptBuilder
    const systemPrompt = roleRef.current?.systemPrompt;

    if (systemPrompt) {
      // Full prompt logging for debugging
      console.log('[Gemini] === FULL SYSTEM PROMPT ===');
      console.log(systemPrompt);
      console.log('[Gemini] === END PROMPT ===');
    }

    // Store mission ID from role if available
    if (roleRef.current?.missionId) {
      missionIdRef.current = roleRef.current.missionId;
    }

    // Create session in Firebase with missionId and teacherId for cost tracking
    sessionIdRef.current = await createSessionUsage(
      userIdRef.current,
      undefined, // sessionHandle
      missionIdRef.current || undefined,
      roleRef.current?.teacherId || undefined
    );

    // If this is a review lesson, mark it as in_progress
    if (roleRef.current?.isReviewLesson && roleRef.current?.reviewId && sessionIdRef.current) {
      await startReviewLesson(userIdRef.current, roleRef.current.reviewId, sessionIdRef.current);
    }

    // Set up function agent context
    isReviewLessonRef.current = roleRef.current?.isReviewLesson === true;
    sessionContextRef.current = {
      level: roleRef.current?.level || 'B1',
      tasks: roleRef.current?.tasks,
    };
    recentTurnsRef.current = []; // Reset turns for new session
    lastUserTranscriptionRef.current = ''; // Reset user transcription

    // Determine if function calling should be enabled
    const enableFunctionCalling = roleRef.current?.functionCallingEnabled !== false;

    // Create the direct client
    // Tasks and isReviewLesson are passed to backend for auto-injected tool instructions
    const client = new GeminiDirectClient({
      userId: userIdRef.current,
      systemPrompt,
      enableFunctionCalling,
      voiceName: voiceNameRef.current,
      tasks: roleRef.current?.tasks,
      isReviewLesson: roleRef.current?.isReviewLesson,
      callbacks: {
        onConnected: () => {
          console.log('[Gemini] Connected');
          setIsConnected(true);
          setIsConnecting(false);
          setConnectionError(null);
          setIsPaused(false);
          setSessionStartTime(new Date());
          sessionMistakesRef.current = []; // Clear tracked mistakes for new session
          startAudioStreaming();
        },
        onDisconnected: () => {
          console.log('[Gemini] Disconnected');
          setIsConnected(false);
          setIsListening(false);
          stopAudioStreaming();
        },
        onAudio: (audioData: ArrayBuffer) => {
          // AI is responding - flush user's transcription buffer first WITH audio
          if (inputTranscriptionBuffer.current.trim()) {
            const userText = inputTranscriptionBuffer.current.trim();
            inputTranscriptionBuffer.current = '';
            lastUserTranscriptionRef.current = userText; // Save for function agent
            console.log('[Gemini] Flushing user message (AI responding):', userText);

            // Extract last 10 seconds of user audio (fast operation)
            const audioBlob = getWebAudioManager().extractRecentAudio(10);
            const msgId = getNextMessageId();

            // Add message immediately (UI responsiveness)
            const message: ChatMessage = {
              id: msgId,
              text: userText,
              isUser: true,
              isWhisper: false,
            };
            setMessages(prev => [...prev, message]);

            // Convert audio to base64 asynchronously and update message
            if (audioBlob) {
              console.log('[Gemini] Extracting user audio:', (audioBlob.size / 1024).toFixed(1), 'KB');
              blobToBase64(audioBlob).then(base64 => {
                console.log('[Gemini] User audio converted to base64:', (base64.length / 1024).toFixed(1), 'KB');
                setMessages(prev => prev.map(m =>
                  m.id === msgId ? { ...m, audioData: base64 } : m
                ));
              }).catch(err => {
                console.warn('[Gemini] Failed to convert user audio:', err);
              });
            }
          }

          // Accumulate AI audio chunk for replay support (store as binary for proper concatenation)
          const bytes = new Uint8Array(audioData);
          aiAudioChunksRef.current.push(bytes);

          // Play the audio
          playAudioResponse(audioData);
        },
        onText: (text: string) => {
          // Text responses are typically the model's "thinking" output
          // We use transcription for actual spoken content, so just log this
          console.log('[Gemini] Received text (not displayed - using transcription):', text.substring(0, 80));
        },
        // Transcription callbacks - show transcribed speech in chat bubbles
        onOutputTranscription: (text: string) => {
          // AI is responding - flush user's transcription buffer first WITH audio
          if (inputTranscriptionBuffer.current.trim()) {
            const userText = inputTranscriptionBuffer.current.trim();
            inputTranscriptionBuffer.current = '';
            lastUserTranscriptionRef.current = userText; // Save for function agent
            console.log('[Gemini] Flushing user message (AI transcription starting):', userText);

            // Extract last 10 seconds of user audio
            const audioBlob = getWebAudioManager().extractRecentAudio(10);
            const msgId = getNextMessageId();

            // Add message immediately
            const message: ChatMessage = {
              id: msgId,
              text: userText,
              isUser: true,
              isWhisper: false,
            };
            setMessages(prev => [...prev, message]);

            // Convert audio to base64 asynchronously
            if (audioBlob) {
              blobToBase64(audioBlob).then(base64 => {
                setMessages(prev => prev.map(m =>
                  m.id === msgId ? { ...m, audioData: base64 } : m
                ));
              }).catch(() => {});
            }
          }
          // Accumulate AI transcription chunks (they come in small pieces)
          outputTranscriptionBuffer.current += text;
          lastTranscriptionTimeRef.current = Date.now();
          console.log('[Gemini] Output transcription chunk:', text);

          // If we're waiting for turn complete, reset the timer (more transcription is coming)
          if (turnCompleteTimeoutRef.current) {
            clearTimeout(turnCompleteTimeoutRef.current);
            turnCompleteTimeoutRef.current = null;
          }
        },
        onInputTranscription: (text: string) => {
          // Accumulate user transcription chunks
          inputTranscriptionBuffer.current += text;
          console.log('[Gemini] Input transcription chunk:', text);
        },
        onTurnComplete: async () => {
          console.log('[Gemini] Turn complete signal received');

          // Clear any existing timeout
          if (turnCompleteTimeoutRef.current) {
            clearTimeout(turnCompleteTimeoutRef.current);
          }

          // Debounce turn complete processing - wait 500ms for any remaining transcription
          // This fixes the issue where audio/transcription arrives after turnComplete signal
          turnCompleteTimeoutRef.current = setTimeout(async () => {
            console.log('[Gemini] Processing turn complete (debounced)');

            // Flush accumulated AI transcription as a single message WITH audio data
            // Filter out Gemini control token bug and function call text
            const cleanedTranscription = outputTranscriptionBuffer.current
              .replace(/<ctrl\d+>/gi, '')  // Remove <ctrl##> tokens
              .replace(/&<ctrl\d+>/gi, '') // Remove &<ctrl##> variant
              .replace(/call:?\s*\w+\([^)]*\)/gi, '') // Remove function call patterns like "call: func(...)" or "call func(...)"
              .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
              .trim();

            if (cleanedTranscription) {
              console.log('[Gemini] Adding AI message:', cleanedTranscription);

              // Combine accumulated AI audio chunks for replay (proper binary concatenation)
              let combinedAudio: string | undefined;
              if (aiAudioChunksRef.current.length > 0) {
                // Calculate total length
                const totalLength = aiAudioChunksRef.current.reduce((sum, chunk) => sum + chunk.length, 0);
                // Concatenate all binary chunks
                const combined = new Uint8Array(totalLength);
                let offset = 0;
                for (const chunk of aiAudioChunksRef.current) {
                  combined.set(chunk, offset);
                  offset += chunk.length;
                }
                // HIGH-004: Use async base64 conversion to prevent UI blocking
                try {
                  combinedAudio = await arrayBufferToBase64Async(combined);
                  console.log('[Gemini] AI audio for replay:', (combinedAudio.length / 1024).toFixed(1), 'KB');
                } catch (error) {
                  console.error('[Gemini] Error converting audio to base64:', error);
                }
              }

              addMessage(cleanedTranscription, false, false, undefined, combinedAudio);

              // Extract full turn audio NOW before buffer gets cleared (for error capture)
              let turnAudioBlob: Blob | null = null;
              try {
                turnAudioBlob = getWebAudioManager().extractErrorAudio();
                console.log('[Gemini] Pre-captured full turn audio:',
                  turnAudioBlob ? `${(turnAudioBlob.size / 1024).toFixed(1)} KB` : 'null');
              } catch (e) {
                // Ignore if audio manager not ready
              }

              // Call function agent in background (non-blocking) with pre-captured audio
              const userText = lastUserTranscriptionRef.current;
              const aiText = cleanedTranscription;
              processTurnForFunctions(userText, aiText, turnAudioBlob).catch(err => {
                console.warn('[FunctionAgent] Background processing error:', err);
              });

              outputTranscriptionBuffer.current = ''; // Clear buffer
              aiAudioChunksRef.current = []; // Clear audio chunks for next turn
              lastUserTranscriptionRef.current = ''; // Clear user transcription for next turn

              // Notify that AI has finished speaking (only when there's actual output)
              // Used for session end timing
              onAiTurnCompleteRef.current?.();
            } else if (aiAudioChunksRef.current.length > 0) {
              // Has audio but no transcription - this is the bug case
              // Wait a bit more for transcription, then clear
              console.log('[Gemini] AI responded with audio but no transcription - possible Gemini bug');
              aiAudioChunksRef.current = [];
              outputTranscriptionBuffer.current = '';
            } else {
              // No transcription and no audio - clear anyway (don't trigger onAiTurnComplete)
              console.log('[Gemini] Turn complete with no output - ignoring for session end');
              aiAudioChunksRef.current = [];
            }

            // Clear the audio buffer to start fresh for user's next turn
            try {
              getWebAudioManager().clearTurnBuffer();
            } catch (e) {
              // Ignore - audio manager may not be ready
            }

            turnCompleteTimeoutRef.current = null;
          }, 500); // Wait 500ms for remaining transcription
        },
        onSessionUpdate: (_handle: string) => {
          console.log('[Gemini] Session handle updated');
          setCanResume(true);
          // Clear resumption failed state on successful update
          setResumptionFailed(false);
        },
        onResumptionFailed: () => {
          console.warn('[Gemini] ⚠️ Session resumption failed - conversation context lost');
          setResumptionFailed(true);
          // Auto-clear after 5 seconds
          setTimeout(() => setResumptionFailed(false), 5000);
        },
        onInterrupted: () => {
          // User interrupted AI's response - stop audio playback and clear buffers
          console.log('[Gemini] 🛑 User interrupted - stopping playback');

          // Clear accumulated AI audio chunks (won't be used now)
          aiAudioChunksRef.current = [];

          // Clear the output transcription buffer (incomplete anyway)
          outputTranscriptionBuffer.current = '';

          // Stop any audio currently playing
          try {
            getWebAudioManager().clearTurnBuffer();
          } catch {
            // Ignore - audio manager may not be ready
          }

          // Update playback state
          setIsPlaying(false);
        },
        onGoAway: (timeRemaining: number) => {
          console.log(`[Gemini] GoAway: ${timeRemaining}s remaining`);
        },
        onError: (error: Error) => {
          console.error('[Gemini] Error:', error);
          setConnectionError(error.message);
          setIsConnecting(false);
        },
        onUsageMetadata: (metadata: UsageMetadata) => {
          // Update local state
          setTokenUsage(prev => ({
            inputTokens: prev.inputTokens + metadata.inputTokens,
            outputTokens: prev.outputTokens + metadata.outputTokens,
            totalTokens: prev.totalTokens + metadata.totalTokens
          }));

          // Update Firebase with userId for new subcollection structure
          if (sessionIdRef.current) {
            updateSessionUsage(sessionIdRef.current, metadata, userIdRef.current);
          }
        }
        // NOTE: onToolCall removed - function calling handled by /api/function-agent
      }
    });

    clientRef.current = client;
    setCanResume(client.canResume);

    try {
      await client.connect();
    } catch (error) {
      console.error('[Gemini] Connection failed:', error);
      setConnectionError((error as Error).message);
      setIsConnecting(false);
    }
  }, [startAudioStreaming, stopAudioStreaming, playAudioResponse, addMessage]);

  /**
   * Pause session - keeps session handle for resume
   */
  const pauseSession = useCallback(async () => {
    if (!clientRef.current) return;

    console.log('[Gemini] Pausing session...');
    console.log('[Gemini] Current message count:', messages.length);
    await stopAudioStreaming();
    await clientRef.current.pause();

    setIsPaused(true);
    setIsConnected(false);
    setIsListening(false);
    setCanResume(true);

    // Finalize current session in Firebase
    if (sessionIdRef.current) {
      await finalizeSessionUsage(
        sessionIdRef.current,
        userIdRef.current,
        clientRef.current.currentSessionHandle || undefined
      );
    }
    console.log('[Gemini] Session paused. Messages preserved:', messages.length);
  }, [stopAudioStreaming, messages.length]);

  /**
   * Resume a paused session
   */
  const resumeSession = useCallback(async () => {
    if (!clientRef.current) return;

    console.log('[Gemini] Resuming session...');
    console.log('[Gemini] Messages before resume:', messages.length);
    console.log('[Gemini] Session handle available:', clientRef.current.currentSessionHandle ? 'yes' : 'no');

    // IMPORTANT: Clear any accumulated audio buffers from before the pause
    // This prevents old audio from playing back on resume
    aiAudioChunksRef.current = [];
    outputTranscriptionBuffer.current = '';
    inputTranscriptionBuffer.current = '';
    lastUserTranscriptionRef.current = '';
    try {
      getWebAudioManager().clearTurnBuffer();
      console.log('[Gemini] Cleared audio buffers for clean resume');
    } catch {
      // Ignore - audio manager may not be ready
    }

    // Create new session tracking with missionId and teacherId
    sessionIdRef.current = await createSessionUsage(
      userIdRef.current,
      clientRef.current.currentSessionHandle || undefined,
      missionIdRef.current || undefined,
      roleRef.current?.teacherId || undefined
    );

    // Reset token usage for new session (but NOT messages!)
    setTokenUsage({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });

    setIsConnecting(true);
    await clientRef.current.resume();
    console.log('[Gemini] Resume initiated. Messages still:', messages.length);
  }, [messages.length]);

  /**
   * Toggle mute state
   */
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      console.log(`[Audio] ${newMuted ? 'Muted' : 'Unmuted'}`);
      return newMuted;
    });
  }, []);

  /**
   * Send text message (visible in chat)
   */
  const sendTextMessage = useCallback((text: string) => {
    if (!isConnected || !clientRef.current) {
      console.warn('[Gemini] Not connected');
      return;
    }

    addMessage(text, true, false);
    clientRef.current.sendText(text);
  }, [isConnected, addMessage]);

  /**
   * Send text to Gemini silently (not shown in chat UI)
   * Used for system commands like wrap-up prompts
   */
  const sendSilentText = useCallback((text: string) => {
    if (!isConnected || !clientRef.current) {
      console.warn('[Gemini] Not connected');
      return;
    }

    console.log('[Gemini] Sending silent text to AI');
    clientRef.current.sendText(text);
  }, [isConnected]);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    messageIdRef.current = 0;
  }, []);

  /**
   * Reconnect to Gemini
   */
  const reconnect = useCallback(async () => {
    if (clientRef.current) {
      await clientRef.current.disconnect();
      clientRef.current = null;
    }
    await stopAudioStreaming();
    await connect();
  }, [connect, stopAudioStreaming]);

  /**
   * Start a completely new session (clear stored handle)
   */
  const startNewSession = useCallback(async () => {
    // Finalize old session
    if (sessionIdRef.current && clientRef.current) {
      await finalizeSessionUsage(
        sessionIdRef.current,
        userIdRef.current,
        clientRef.current.currentSessionHandle || undefined
      );
    }

    // Clear stored session handle
    if (clientRef.current) {
      clientRef.current.clearStoredSessionHandle();
    }

    // Reset state
    clearMessages();
    setTokenUsage({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });
    setCanResume(false);
    sessionMistakesRef.current = []; // Clear tracked mistakes

    // Reconnect fresh
    await reconnect();
  }, [clearMessages, reconnect]);

  /**
   * Update system prompt with new role
   */
  const updateSystemPrompt = useCallback((role: AIRole) => {
    roleRef.current = role;
    reconnect();
  }, [reconnect]);

  /**
   * Nudge Gemini to respond when it's silent
   * This signals activity end to trigger a response when VAD fails
   */
  const nudgeResponse = useCallback(() => {
    if (!isConnected || !clientRef.current) {
      console.warn('[Gemini] Cannot nudge - not connected');
      return;
    }

    // First try activity end signal
    clientRef.current.nudgeResponse();

    // If still no response after 2 seconds, force with turnComplete
    setTimeout(() => {
      if (clientRef.current?.connected) {
        clientRef.current.forceResponse();
      }
    }, 2000);
  }, [isConnected]);

  // Track the last connected system prompt to prevent unnecessary reconnections
  const lastConnectedPromptRef = useRef<string | null>(null);

  /**
   * Connect when role is available (CRIT-002: with isCancelled flag to prevent race conditions)
   *
   * IMPORTANT: We use lastConnectedPromptRef to prevent mid-conversation restarts.
   * React re-renders can cause object reference changes even when content is the same,
   * which would trigger this effect. By comparing the actual prompt string, we only
   * reconnect when the prompt content has truly changed.
   *
   * See: https://ai.google.dev/gemini-api/docs/live-session
   */
  useEffect(() => {
    // Only connect when we have a valid role with systemPrompt
    if (!initialRole?.systemPrompt) {
      return;
    }

    // Wait for user document to load so we have the correct voice preference
    if (userDocumentReady === false) {
      return;
    }

    // CRITICAL: Skip reconnection if already connected with the same prompt content
    // This prevents mid-conversation restarts when React re-renders
    if (clientRef.current?.connected && lastConnectedPromptRef.current === initialRole.systemPrompt) {
      console.log('[Gemini] Already connected with same prompt, skipping reconnect');
      return;
    }

    let isCancelled = false;

    // Update role ref BEFORE connecting
    roleRef.current = initialRole;

    // Async connection with cancellation check
    const initConnection = async () => {
      if (isCancelled) return;
      await connect();
      // Track what prompt we connected with
      if (!isCancelled) {
        lastConnectedPromptRef.current = initialRole.systemPrompt || null;
      }
    };

    initConnection();

    return () => {
      isCancelled = true;
      stopAudioStreaming();

      // Clear turn complete debounce timeout on unmount
      if (turnCompleteTimeoutRef.current) {
        clearTimeout(turnCompleteTimeoutRef.current);
        turnCompleteTimeoutRef.current = null;
      }

      // Finalize session on unmount
      if (sessionIdRef.current && clientRef.current) {
        finalizeSessionUsage(
          sessionIdRef.current,
          userIdRef.current,
          clientRef.current.currentSessionHandle || undefined
        );
      }

      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }

      // Clear the tracked prompt on cleanup so next mount will connect
      lastConnectedPromptRef.current = null;
    };
  }, [initialRole?.systemPrompt, userDocumentReady]);

  /**
   * Update role ref when initialRole changes
   */
  useEffect(() => {
    if (initialRole) {
      roleRef.current = initialRole;
    }
  }, [initialRole]);

  return {
    // Connection state
    isConnected,
    isConnecting,
    connectionError,

    // Listening state
    isListening,
    isMuted,

    // Pause state
    isPaused,
    canResume,

    // Playback state
    isPlaying,

    // Token usage
    tokenUsage,

    // Messages
    messages,

    // Session summary (from function call)
    sessionSummary,

    // Newly earned badges
    newBadges,

    // Session timing
    sessionStartTime,

    // Session timed out (Gemini didn't respond)
    sessionTimedOut,

    // Session resumption failed (context lost)
    resumptionFailed,

    // Audio playback for review lessons
    audioToPlay,
    onAudioPlaybackComplete,
    onAudioPlaybackError,

    // Session mistakes (for automatic session summary)
    sessionMistakes: sessionMistakesRef.current,

    // Actions
    toggleMute,
    disconnect: async () => {
      // Clean disconnect - stop audio, disconnect client, update state
      await stopAudioStreaming();
      if (clientRef.current) {
        await clientRef.current.disconnect();
        clientRef.current = null;
      }
      if (sessionIdRef.current) {
        await finalizeSessionUsage(sessionIdRef.current, userIdRef.current);
      }
      setIsConnected(false);
      setIsListening(false);
    },
    pauseSession,
    resumeSession,
    sendTextMessage,
    sendSilentText,
    clearMessages,
    reconnect,
    startNewSession,
    updateSystemPrompt,
    clearSessionSummary,
    clearNewBadges,
    nudgeResponse,

    // Exposed for session lifecycle hook
    startAudioStreaming,
    stopAudioStreaming,
  };
}
